import { AxiosPromise } from 'axios';
import { SearchParams } from '../model/searchParams';
import trainerPayrollFuncs, {
  TrainerPayroll,
  Value,
} from '../model/trainerPayroll';
import { instance, URLS } from '.';
import ODataFilterBuilder from 'odata-filter-builder';
import { DefaultBackendHelper } from '../utils/backendHelper';
import { CONST } from '../utils/constants';
import {
  getTrainerHoursStatusText,
  TrainerHoursStatusEnum,
} from '../model/trainerHours';

export interface BackendTrainerPayroll {
  getTrainerPayrolls: (
    searchParams: SearchParams,
    year?: number,
    month?: number,
    trainerId?: string,
    status?: TrainerHoursStatusEnum,
    departmentId?: string,
    locationId?: string
  ) => AxiosPromise<Value>;
  updateTrainerPayroll: (trainerPayroll: TrainerPayroll) => AxiosPromise;
  exportTrainerPayrolls: (
    year?: number,
    months?: number[],
    trainerIds?: string[],
    statuses?: TrainerHoursStatusEnum[],
    markExportedTrainerHoursPaid?: boolean,
    exportPreviouslyExportedTrainerHours?: boolean,
    lang?: string
  ) => AxiosPromise;
  countTrainerPayrollsForExport: (
    year?: number,
    months?: number[],
    trainerIds?: string[],
    statuses?: TrainerHoursStatusEnum[],
    exportPreviouslyExportedTrainerHours?: boolean
  ) => AxiosPromise<number>;
}

export const defaultBackendTrainerPayroll: BackendTrainerPayroll = {
  getTrainerPayrolls(
    searchParams: SearchParams,
    year?: number,
    month?: number,
    trainerId?: string,
    status?: TrainerHoursStatusEnum,
    departmentId?: string,
    locationId?: string
  ): AxiosPromise<Value> {
    const odfb = ODataFilterBuilder('and');

    if (year !== undefined) {
      odfb.eq(CONST.Year, year, false);
    }

    if (month !== undefined) {
      odfb.eq(CONST.Month, month, false);
    }

    if (trainerId !== undefined) {
      odfb.eq(CONST.TrainerId, trainerId, false);
    }

    if (status !== undefined) {
      odfb.eq(CONST.Status, getTrainerHoursStatusText(status));
    }

    let baseUrl;
    if (departmentId !== undefined) {
      baseUrl = `${URLS.trainerPayrollOdata}/GetInDepartment(departmentId=${departmentId})`;
    } else if (locationId !== undefined) {
      baseUrl = `${URLS.trainerPayrollOdata}/GetInLocation(locationId=${locationId})`;
    } else {
      baseUrl = URLS.trainerPayrollOdata;
    }

    const url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );

    return instance.get<Value>(url);
  },
  updateTrainerPayroll(trainerPayroll: TrainerPayroll): AxiosPromise {
    const costCenterString =
      trainerPayroll.costCenter === null ||
      trainerPayroll.costCenter === undefined
        ? 'null'
        : trainerPayroll.costCenter === ''
        ? 'empty'
        : encodeURIComponent(trainerPayroll.costCenter);

    const url = `${URLS.trainerPayroll}/${trainerPayroll.trainerId}/${
      trainerPayroll.year
    }/${trainerPayroll.month}/${
      trainerPayroll.courseId ?? CONST.emptyGuid
    }/${costCenterString}`;
    return instance.put(url, trainerPayrollFuncs.toAPI(trainerPayroll));
  },
  exportTrainerPayrolls(
    year?: number,
    months?: number[],
    trainerIds?: string[],
    statuses?: TrainerHoursStatusEnum[],
    markExportedTrainerHoursPaid?: boolean,
    exportPreviouslyExportedTrainerHours?: boolean,
    lang?: string
  ): AxiosPromise {
    const url = `${URLS.trainerPayroll}/Export`;
    return instance.post(
      url,
      {
        Year: year,
        Months: months,
        TrainerIds: trainerIds,
        Statuses: statuses,
        MarkExportedTrainerHoursPaid: markExportedTrainerHoursPaid ?? false,
        ExportPreviouslyExportedTrainerHours:
          exportPreviouslyExportedTrainerHours ?? false,
      },
      {
        responseType: 'blob',
        params: {
          lang: lang,
        },
      }
    );
  },
  countTrainerPayrollsForExport(
    year?: number,
    months?: number[],
    trainerIds?: string[],
    statuses?: TrainerHoursStatusEnum[],
    exportPreviouslyExportedTrainerHours?: boolean
  ): AxiosPromise<number> {
    const url = `${URLS.trainerPayroll}/CountExportedPayrolls`;
    return instance.post<number>(url, {
      Year: year,
      Months: months,
      TrainerIds: trainerIds,
      Statuses: statuses,
      ExportPreviouslyExportedTrainerHours:
        exportPreviouslyExportedTrainerHours ?? false,
    });
  },
};
